import { useCallback } from 'react';
import { useTranslation } from 'next-i18next';
import tw from 'twin.macro';
import { EyeSlashIcon } from '@heroicons/react/24/solid';

import { BaseModal, ButtonV2, Title } from '@restworld/ui-ds';

interface PutOfflineJobPositionConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
  isSubscriptionFeedbacksSubmitted?: boolean;
}

export function PutOfflineJobPositionConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  isSubscriptionFeedbacksSubmitted = false,
}: PutOfflineJobPositionConfirmationModalProps) {
  const { t } = useTranslation('job-positions-page');

  const handleConfirm = useCallback(() => {
    onConfirm();
  }, [onConfirm]);

  const titleEl = (
    <Title
      content={t('put_offline_confirmation_title')}
      fontSize="extraLarge"
      fontWeight="semibold"
      textColor="neutral-700"
      twStyle={tw`text-center leading-[30px] px-4 sm:px-0`}
    />
  );

  const footerEl = (
    <div tw="flex flex-col justify-center items-center gap-4 w-full">
      <ButtonV2
        onClick={handleConfirm}
        fullwidth
        loading={isLoading}
        StartIcon={isSubscriptionFeedbacksSubmitted ? EyeSlashIcon : undefined}
        variant={isSubscriptionFeedbacksSubmitted ? 'secondary-danger' : 'primary'}
        disabled={isLoading}
      >
        {isSubscriptionFeedbacksSubmitted
          ? t('nps_confirm_and_go_offline_button')
          : t('put_offline_confirmation_continue_button')}
      </ButtonV2>
      <ButtonV2 variant="tertiary" fullwidth onClick={() => onClose()} disabled={isLoading}>
        {t('put_offline_confirmation_cancel_button')}
      </ButtonV2>
    </div>
  );

  return (
    <BaseModal
      showMobileGestures
      modalStyles={[tw`w-full max-w-[640px] lg:(max-w-screen-sm)`]}
      open={isOpen}
      onClose={() => onClose()}
      title={titleEl}
      footer={footerEl}
    >
      <div tw="flex flex-col justify-center items-center gap-5 w-full text-center mt-6">
        <Title
          content={t('put_offline_confirmation_description')}
          fontSize="base"
          fontWeight="medium"
          textColor="neutral-700"
          twStyle={tw`whitespace-pre-line`}
        />
      </div>
    </BaseModal>
  );
}

export default PutOfflineJobPositionConfirmationModal;
